import React, { useState, useCallback, useEffect, useRef } from "react";
import { CSVLink } from "react-csv";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IoChevronDownSharp } from "react-icons/io5";
import { createColumnHelper } from "@tanstack/react-table";

import {
  SCREEN_PATH,
  EMPTY_STRING,
  PAYLOAD_KEYS,
  agentTabs,
  adminReduxConstants,
  agentApprovalStatus,
  agentApprovalsExportHeaders,
  agentCurrentListExportHeaders,
  PAYMENT_DISABLED_MESSAGE,
  CONFIRMATION_MESSAGE,
} from "../../../constants";
import { ERROR_TOAST } from "../../../constants/toast.constants";
import { ViewIcon } from "../../../assets";
import { APPROVAL_STATUS, EXPORT_TYPE, STATUS_LABEL } from "../../../system";
import {
  fileName,
  getSortBy,
  customToast,
  getPageNumber,
  buildFilterQuery,
  getOptionsFromEnums,
  customStringSorting,
} from "../../../shared/utility";
import ReactTable from "../../../shared/components/tables/ReactTable";
import SearchField from "../../../shared/components/search/SearchField";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import ExportDropdown from "../../../shared/components/export/ExportDropdown";
import ConfirmationModal from "../../../shared/components/modals/ConfirmationModal";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions, systemActions } from "../../../redux/actions";
import AgentApprovalModal from "./AgentApprovalModal";
import AgentRejectionModal from "./AgentRejectionModal";

const { FILTER_QUERY, PAGE_SIZE, PAGE_NUMBER, SEARCH } = PAYLOAD_KEYS;
const {
  GET_AGENTS,
  AGENTS_PAGINATION,
  APPROVALS_PAGINATION,
  GET_AGENTS_APPROVALS_EXPORT_ALL_DATA,
  GET_AGENTS_CURRENT_LIST_EXPORT_ALL_DATA,
} = adminReduxConstants;

function Agents({
  spinnerActions,
  agents,
  agentsPagination,
  approvals,
  approvalsPagination,
  getApprovedAgents,
  getAgentsApprovals,
  updatePagination,
  updateApprovalStatus,
  updateAgentStatus,
  updateAgentPaymentStatus,
  agentsApprovalsAllItemsForExport,
  agentsCurrentListAllItemsForExport,
  resetAllExportItems,
  getApprovedAgentsItemsForExport,
  getAgentsApprovalsItemsForExport,
  isConfirmationModal,
  setIsConfirmationModal,
  setSelectedRowId,
  selectedRowId,
}) {
  const csvLinkRef = useRef();
  const navigate = useNavigate();

  const [exportData, setExportData] = useState([]);
  const [sorting, setSorting] = useState({ id: "id", desc: true });
  const [currentTab, setCurrentTab] = useState(agentTabs.CURRENT_LIST);
  const [showAgentApprovalModal, setShowAgentApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [currentAgent, setCurrentAgent] = useState();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showPayementConfirmationModal, setShowPayementConfirmationModal] =
    useState(false);

  const openModal = (setModal) => setModal(true);
  const closeModal = (setModal) => {
    setModal(false);
    setCurrentAgent();
  };

  const getCurrentPagination = () =>
    currentTab === agentTabs.CURRENT_LIST
      ? agentsPagination
      : approvalsPagination;

  const updateCurrentPagination = (data, type) => {
    const pagination = getCurrentPagination();
    const payload = {
      ...pagination,
      [SEARCH]: type === SEARCH ? data : pagination.search,
      [PAGE_SIZE]: type === PAGE_SIZE ? data : pagination.pageSize,
      [PAGE_NUMBER]: getPageNumber(type, data, pagination),
    };

    updatePagination(
      payload,
      currentTab === agentTabs.CURRENT_LIST
        ? AGENTS_PAGINATION
        : APPROVALS_PAGINATION
    );
  };

  const handleChangeStatusFilter = (e) => {
    updatePagination(
      {
        ...approvalsPagination,
        filterData: e.target.value,
      },
      APPROVALS_PAGINATION
    );
  };

  const approveAgent = () => {
    const reqData = {
      id: currentAgent.id,
      isApprove: true,
      comment: EMPTY_STRING,
    };
    updateApprovalStatus(reqData, () => {
      getAgentsApprovals(approvalsPagination);

      closeModal(setShowAgentApprovalModal);
    });
  };
  const rejectAgent = (comment) => {
    if (!comment) {
      customToast("Comments are required", ERROR_TOAST);
      return;
    }
    const reqData = {
      id: currentAgent.id,
      isApprove: false,
      comment,
    };
    updateApprovalStatus(reqData, () => {
      getAgentsApprovals(approvalsPagination);
      closeModal(setShowRejectModal);
      closeModal(setShowAgentApprovalModal);
    });
  };

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    [spinnerActions]
  );

  const columnHelper = createColumnHelper();

  const getColumns = () => {
    return [
      columnHelper.accessor("name", {
        header: <div className="column-head">Name</div>,
        cell: ({ row: { original } }) => (
          <div title={original.name} className="line-clamp">
            {original.name}
          </div>
        ),
        enableSorting: true,
        sortingFn: customStringSorting,
        size: 150,
      }),
      columnHelper.accessor("email", {
        header: <div className="column-head">Email</div>,
        cell: ({ row: { original } }) => (
          <div title={original.email} className="line-clamp">
            {original.email}
          </div>
        ),
        enableSorting: true,
        sortingFn: customStringSorting,
        size: 150,
      }),
      columnHelper.accessor("phoneNumber", {
        header: <div className="column-head">Phone Number</div>,
        cell: ({ row: { original } }) => <div>+{original.phoneNumber}</div>,
        size: 120,
        enableSorting: true,
      }),
      ...(currentTab === agentTabs.CURRENT_LIST
        ? [
            columnHelper.accessor("propertiesCount", {
              header: (
                <div className="column-head text-center">
                  Number of Listings
                </div>
              ),
              cell: ({ row: { original } }) => (
                <div className="text-center">{original.propertiesCount}</div>
              ),
              size: 120,
              enableSorting: true,
            }),
          ]
        : []),
      ...(currentTab === agentTabs.APPROVALS
        ? [
            columnHelper.accessor("companyName", {
              header: <div className="column-head">Agency Name</div>,
              cell: ({ row: { original } }) => (
                <div>{original?.companyName}</div>
              ),
              size: 100,
              enableSorting: true,
              sortingFn: customStringSorting,
            }),
            columnHelper.accessor("licenseNumber", {
              header: <div className="column-head">Licence Number</div>,
              cell: ({ row: { original } }) => (
                <div>{original?.licenseNumber}</div>
              ),
              size: 120,
              enableSorting: true,
              sortingFn: customStringSorting,
            }),
          ]
        : []),
      ...(currentTab === agentTabs.APPROVALS
        ? [
            columnHelper.accessor("approvalStatus", {
              header: <div className="column-head">Status</div>,
              cell: ({ row: { original } }) => (
                <div>
                  {original.approvalStatus === APPROVAL_STATUS.REJECTED ? (
                    <div className="status-rejected">Rejected</div>
                  ) : (
                    <div className="status-pending">Pending</div>
                  )}
                </div>
              ),
              size: 80,
              enableSorting: true,
            }),
          ]
        : [
            columnHelper.accessor("status", {
              header: <div className="column-head">Status</div>,
              cell: ({ row: { original } }) => (
                <div className="admin-switch-status">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={original.status}
                      onChange={(e) => {
                        setSelectedRowId(original.id);
                        setShowConfirmationModal(true);
                      }}
                    />
                  </div>
                </div>
              ),
              size: 70,
              enableSorting: true,
            }),
            columnHelper.accessor("isPaymentDisabled", {
              header: <div className="column-head">Payment Disabled</div>,
              cell: ({ row: { original } }) => (
                <div className="admin-switch-status">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={original.isPaymentDisabled}
                      onChange={(e) => {
                        setSelectedRowId(original.id);
                        setShowPayementConfirmationModal(true);
                      }}
                    />
                  </div>
                </div>
              ),
              size: 70,
              enableSorting: true,
            }),
          ]),
      columnHelper.accessor("action", {
        header: <div className="column-head text-end">Action</div>,
        enableSorting: false,
        cell: ({ row: { original } }) => (
          <div className="text-end pe-3">
            <img
              className="pointer"
              src={ViewIcon}
              onClick={() => {
                if (currentTab === agentTabs.CURRENT_LIST) {
                  navigate(`${SCREEN_PATH.ADMIN_AGENTS}/${original.id}`);
                } else {
                  setCurrentAgent(original);
                  openModal(setShowAgentApprovalModal);
                }
              }}
              alt={EMPTY_STRING}
            />
          </div>
        ),
        size: 100,
      }),
    ];
  };

  const onPaymentStatusConfirmation = () => {
    setShowPayementConfirmationModal(false);
    updateAgentPaymentStatus(selectedRowId, { isConfirm: false }, () =>
      getApprovedAgents({ ...agentsPagination, orderBy: getSortBy(sorting) })
    );
  };

  const onStatusConfirmation = () => {
    setShowConfirmationModal(false);
    updateAgentStatus(selectedRowId, () =>
      getApprovedAgents({
        ...agentsPagination,
        orderBy: getSortBy(sorting),
      })
    );
  };

  const onReConfirmation = () => {
    setIsConfirmationModal(false);
    updateAgentPaymentStatus(selectedRowId, { isConfirm: true }, () =>
      getApprovedAgents({ ...agentsPagination, orderBy: getSortBy(sorting) })
    );
  };

  const isCurrentList = () => {
    return currentTab === agentTabs.CURRENT_LIST;
  };

  // Used to export CSV
  const exportCSV = () => {
    setTimeout(() => {
      csvLinkRef.current.link.click();
    }, 1000);
  };

  const updatedCurrentList = (items) => {
    return items.map((item) => {
      return {
        ...item,
        phoneNumber: `\t+${item.phoneNumber}`,
        status: item.status ? STATUS_LABEL.ACTIVE : STATUS_LABEL.INACTIVE,
      };
    });
  };

  const updatedApprovalList = (items) => {
    return items.map((item) => {
      return {
        ...item,
        phoneNumber: `\t+${item.phoneNumber}`,
        approvalStatus:
          agentApprovalStatus[item.approvalStatus] || EMPTY_STRING,
        licenseNumber: `\t${item.licenseNumber}`,
      };
    });
  };

  // Get data for export
  const getExportData = (type) => {
    if (type === EXPORT_TYPE.GRID) {
      const data = isCurrentList()
        ? updatedCurrentList(agents)
        : updatedApprovalList(approvals);
      setExportData(data);
      exportCSV();
    }
    if (type === EXPORT_TYPE.ALL) {
      if (isCurrentList()) {
        const updatedPayload = {
          ...agentsPagination,
          [PAGE_NUMBER]: 0,
          [PAGE_SIZE]: agentsPagination.total,
          [FILTER_QUERY]: getCurrentListFilterQuery(agentsPagination.search),
          orderBy: getSortBy(sorting),
        };
        getApprovedAgentsItemsForExport(updatedPayload);
      } else {
        const updatedPayload = {
          ...approvalsPagination,
          [PAGE_NUMBER]: 0,
          [PAGE_SIZE]: approvalsPagination.total,
          [FILTER_QUERY]: getApprovalListFilterQuery(
            approvalsPagination.search,
            approvalsPagination.filterData
          ),
          orderBy: getSortBy(sorting),
        };
        getAgentsApprovalsItemsForExport(updatedPayload);
      }
    }
  };

  const currentData =
    currentTab === agentTabs.CURRENT_LIST ? agents : approvals;

  const getCurrentListFilterQuery = (data) => {
    const searchFields = ["Name", "Email", "PhoneNumber"];
    return buildFilterQuery(data, searchFields);
  };

  const getApprovalListFilterQuery = (data, filterData) => {
    const searchFields = [
      "Name",
      "Email",
      "PhoneNumber",
      "CompanyName",
      "LicenseNumber",
    ];
    let filter = buildFilterQuery(data, searchFields);
    // Combine the filterQuery and filterData
    if (filterData !== EMPTY_STRING) {
      filter =
        filter !== EMPTY_STRING ? `(${filter}) && (${filterData})` : filterData;
    }
    return filter;
  };

  useEffect(() => {
    return () => {
      const payload = {
        [PAGE_SIZE]: 10,
        [PAGE_NUMBER]: 0,
        [SEARCH]: EMPTY_STRING,
        [FILTER_QUERY]: EMPTY_STRING,
        filterData: EMPTY_STRING,
      };

      updatePagination(payload, AGENTS_PAGINATION);
      updatePagination(payload, APPROVALS_PAGINATION);
    };
  }, []);

  useEffect(() => {
    if (currentTab === agentTabs.CURRENT_LIST) {
      const updatedPayload = {
        ...agentsPagination,
        [FILTER_QUERY]: getCurrentListFilterQuery(agentsPagination.search),
        orderBy: getSortBy(sorting),
      };
      getApprovedAgents(updatedPayload);
    } else {
      const updatedPayload = {
        ...approvalsPagination,
        [FILTER_QUERY]: getApprovalListFilterQuery(
          approvalsPagination.search,
          approvalsPagination.filterData
        ),
        orderBy: getSortBy(sorting),
      };
      getAgentsApprovals(updatedPayload);
    }

    // eslint-disable-next-line
  }, [
    currentTab,
    agentsPagination.search,
    agentsPagination.pageNumber,
    agentsPagination.pageSize,
    approvalsPagination.search,
    approvalsPagination.pageNumber,
    approvalsPagination.pageSize,
    approvalsPagination.filterData,
    sorting,
  ]);

  useEffect(() => {
    if (agentsCurrentListAllItemsForExport.length) {
      setExportData(updatedCurrentList(agentsCurrentListAllItemsForExport));
      exportCSV();
      resetAllExportItems(GET_AGENTS_CURRENT_LIST_EXPORT_ALL_DATA);
    }
    // eslint-disable-next-line
  }, [agentsCurrentListAllItemsForExport]);

  useEffect(() => {
    if (agentsApprovalsAllItemsForExport.length) {
      setExportData(updatedApprovalList(agentsApprovalsAllItemsForExport));
      exportCSV();
      resetAllExportItems(GET_AGENTS_APPROVALS_EXPORT_ALL_DATA);
    }
    // eslint-disable-next-line
  }, [agentsApprovalsAllItemsForExport]);

  return (
    <MainContentWrapper>
      {spinner(GET_AGENTS) > -1 && <MainSpinner className="admin-spinner" />}
      <ConfirmationModal
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => onStatusConfirmation()}
        show={showConfirmationModal}
        message={CONFIRMATION_MESSAGE}
      />
      <ConfirmationModal
        onCancel={() => setShowPayementConfirmationModal(false)}
        onConfirm={() => onPaymentStatusConfirmation()}
        show={showPayementConfirmationModal}
        message={CONFIRMATION_MESSAGE}
      />
      <ConfirmationModal
        onCancel={() => setIsConfirmationModal(false)}
        onConfirm={() => onReConfirmation()}
        show={isConfirmationModal}
        message={PAYMENT_DISABLED_MESSAGE}
      />
      <AgentApprovalModal
        agentId={currentAgent?.id}
        show={showAgentApprovalModal}
        closeModal={() => closeModal(setShowAgentApprovalModal)}
        openRejectModal={() => openModal(setShowRejectModal)}
        approveAgent={approveAgent}
      />
      <AgentRejectionModal
        show={showRejectModal}
        closeModal={() => closeModal(setShowRejectModal)}
        rejectAgent={rejectAgent}
      />
      <div className="admin-page-heading">Agents</div>
      <div className="agent-tabs">
        {Object.values(agentTabs).map((tab) => (
          <div
            key={tab}
            className={`tab text-center pointer d-inline-block ${
              currentTab === tab ? "selected-tab" : EMPTY_STRING
            }`}
            onClick={() => {
              setCurrentTab(tab);
              setSorting({ id: "id", desc: true });
            }}
          >
            {tab === agentTabs.CURRENT_LIST ? "Current List" : "Approvals"}
            <div className="trapezoid" />
          </div>
        ))}
      </div>
      <div className="d-flex align-items-center flex-wrap column-gap-2 column-gap-sm-4 justify-content-between">
        <div className="d-flex align-items-center flex-wrap column-gap-2">
          <SearchField
            handleSearch={(val) => updateCurrentPagination(val, SEARCH)}
            value={getCurrentPagination().search}
            className={
              currentTab === agentTabs.APPROVALS ? "w-auto" : EMPTY_STRING
            }
          />
          {currentTab === agentTabs.APPROVALS && (
            <div className="position-relative">
              <select
                className="search-field search-field-select ps-2 pe-4 mt-4 w-auto"
                value={approvalsPagination.filterData}
                onChange={handleChangeStatusFilter}
              >
                <option value={EMPTY_STRING}>Status</option>
                {getOptionsFromEnums(agentApprovalStatus).map((option) => (
                  <option value={`approvalStatus=${option.value}`}>
                    {option.label}
                  </option>
                ))}
              </select>
              <IoChevronDownSharp className="status-filter-icon mt-4" />
            </div>
          )}
        </div>
        <ExportDropdown className="mt-3 mt-sm-4" onExport={getExportData} />
      </div>
      <ReactTable
        data={currentData}
        columns={getColumns()}
        paginationData={{
          currentPage: getCurrentPagination().pageNumber,
          totalItems: getCurrentPagination().total ?? 0,
          pageSize: getCurrentPagination().pageSize,
          onPageChange: (page) => updateCurrentPagination(page, PAGE_NUMBER),
          onPageSizeChange: (size) => updateCurrentPagination(size, PAGE_SIZE),
        }}
        sortedBy={sorting}
        onColumnSort={(sortBy) => setSorting(sortBy)}
      />
      <CSVLink
        target="_blank"
        ref={csvLinkRef}
        className="hidden"
        headers={
          isCurrentList()
            ? agentCurrentListExportHeaders
            : agentApprovalsExportHeaders
        }
        data={exportData}
        filename={fileName(
          isCurrentList() ? "Agents Current List" : "Agents Approvals"
        )}
      />
    </MainContentWrapper>
  );
}

const mapStateToProps = (state) => ({
  agents: state.adminReducer.agents,
  approvals: state.adminReducer.approvals,
  spinnerActions: state.systemReducer.spinnerActions,
  agentsPagination: state.adminReducer.agentsPagination,
  approvalsPagination: state.adminReducer.approvalsPagination,
  agentsApprovalsAllItemsForExport:
    state.adminReducer.agentsApprovalsAllItemsForExport,
  agentsCurrentListAllItemsForExport:
    state.adminReducer.agentsCurrentListAllItemsForExport,
  isConfirmationModal: state.systemReducer.isConfirmationModal,
  selectedRowId: state.systemReducer.selectedRowId,
});

const mapDispatchToProps = (dispatch) => ({
  getApprovedAgents: (data) => dispatch(adminActions.getApprovedAgents(data)),
  getApprovedAgentsItemsForExport: (data) =>
    dispatch(adminActions.getApprovedAgentsItemsForExport(data)),
  getAgentsApprovals: (data) => dispatch(adminActions.getAgentsApprovals(data)),
  getAgentsApprovalsItemsForExport: (data) =>
    dispatch(adminActions.getAgentsApprovalsItemsForExport(data)),
  updateApprovalStatus: (data, func) =>
    dispatch(adminActions.updateApprovalStatus(data, func)),
  updateAgentStatus: (data, id, func) =>
    dispatch(adminActions.updateAgentStatus(data, id, func)),
  updateAgentPaymentStatus: (id, body, func) =>
    dispatch(adminActions.updateAgentPaymentStatus(id, body, func)),
  updatePagination: (data, type) =>
    dispatch(adminActions.updatePagination(data, type)),
  resetAllExportItems: (type) =>
    dispatch(adminActions.resetAllExportItems(type)),
  setIsConfirmationModal: (flag) =>
    dispatch(systemActions.setIsConfirmationModal(flag)),
  setSelectedRowId: (id) => dispatch(systemActions.setSelectedRowId(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Agents);
